/**
 * header-lazy-submenu.js
 *
 * Hydrates the empty `<div data-lazy-submenu data-handle="…">`
 * placeholders that `snippets/navigation.liquid` emits when called with
 * `lazy_submenus: true`.
 *
 * Architecture:
 *   1. Header section ships only the top-level nav + empty placeholders
 *      (saves ~50 KB raw / ~7 KB gzipped of inline submenu HTML per
 *      page load — measured 2026-05-06 against home, PDP, collection).
 *   2. After window.load (when the page is interactive and idle), this
 *      script fetches `/?sections=header-submenus` once. Cached for the
 *      tab's lifetime.
 *   3. The fetched HTML is parsed; each `<div data-submenu-handle="…">`
 *      block is injected into the matching placeholder's outerHTML so
 *      the resulting DOM exactly matches what the inline render would
 *      have produced.
 *   4. Hover handlers in theme.js's `Navigation()` query the DOM live
 *      via `document.querySelector("[data-submenu]")` at hover time, so
 *      they pick up the injected nodes without any re-init.
 *
 * Idempotent — only fetches once per page load. No-op if no placeholders
 * are present (e.g. on pages with the legacy inline render).
 *
 * Failure mode: if the fetch errors (network, 5xx, CORS), the
 * placeholders stay empty. The top-level nav still works — clicking a
 * top-level link navigates as a normal `<a>`. The hover-to-reveal
 * submenu just won't appear. Acceptable degradation.
 *
 * Trigger strategy:
 *   - PRIMARY: prefetch shortly after window.load, wrapped in
 *     requestIdleCallback so it doesn't compete with critical work.
 *     This means by the time the user moves to the nav, the submenus
 *     are already present.
 *   - FALLBACK: if the user hovers/touches a trigger before the prefetch
 *     resolves (rare — load + idle is usually <2 s), the same fetch is
 *     kicked off synchronously. The dropdown will pop in once the fetch
 *     resolves; before that, `closeAll()` keeps the empty placeholder
 *     hidden so the user just sees nothing happen on first hover.
 */
(function () {
  if (window.__headerLazySubmenuInit) return;
  window.__headerLazySubmenuInit = true;

  var ENDPOINT = '/?sections=header-submenus';
  var fetchPromise = null;
  var hydrated = false;

  function getPlaceholders() {
    return document.querySelectorAll('[data-lazy-submenu][data-handle]');
  }

  function hydrateFromHTML(html) {
    if (hydrated) return;
    var placeholders = getPlaceholders();
    if (!placeholders.length) {
      hydrated = true;
      return;
    }

    // Section Rendering API returns either raw HTML or a JSON object
    // when `?sections=foo` is used (vs `?section_id=foo`). The JSON
    // form is `{ "header-submenus": "<html>" }`. Detect both.
    var sectionHTML = html;
    if (typeof html === 'string' && html.charAt(0) === '{') {
      try {
        var parsed = JSON.parse(html);
        sectionHTML = parsed['header-submenus'] || '';
      } catch (e) {
        // Not JSON — assume raw HTML, fall through.
      }
    }
    if (!sectionHTML) {
      hydrated = true;
      return;
    }

    var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');

    placeholders.forEach(function (placeholder) {
      var handle = placeholder.getAttribute('data-handle');
      if (!handle) return;
      var src = doc.querySelector(
        '[data-submenu-handle="' + cssEscape(handle) + '"]'
      );
      if (!src) return;
      // Replace the placeholder with the inner content of the source
      // wrapper (which is the actual `.navigation__submenu` div the
      // inline render would have produced). Using outerHTML replacement
      // via a temporary container preserves attributes correctly.
      var tmp = document.createElement('div');
      tmp.innerHTML = src.innerHTML;
      var injected = tmp.firstElementChild;
      if (injected) {
        placeholder.parentNode.replaceChild(injected, placeholder);
      }
    });

    // Mirror what Stiletto's `Navigation()` does at init (theme.js
    // ~L7138): set `--submenu-item-height` on the navigation container
    // so depth-2 submenus align vertically against their depth-1
    // parents. This is normally captured at page load — re-run it now
    // that the submenus actually exist in the DOM.
    try {
      var navContainer = document.querySelector('[data-navigation]');
      if (navContainer) {
        var firstItem = navContainer.querySelector(
          '.navigation__submenu .navigation__submenu-item'
        );
        if (firstItem && firstItem.clientHeight) {
          navContainer.style.setProperty(
            '--submenu-item-height',
            firstItem.clientHeight + 'px'
          );
        }
      }
    } catch (e) {
      // Non-fatal — submenu just won't have the alignment offset.
    }

    hydrated = true;
  }

  function cssEscape(s) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
    // Minimal fallback: only handle handles which are ASCII slug-safe.
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function fetchSubmenus() {
    if (fetchPromise) return fetchPromise;
    fetchPromise = fetch(ENDPOINT, {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' },
    })
      .then(function (res) {
        if (!res.ok) throw new Error('header-submenus fetch failed: ' + res.status);
        return res.text();
      })
      .then(hydrateFromHTML)
      .catch(function () {
        // Reset so a later hover can retry once.
        fetchPromise = null;
      });
    return fetchPromise;
  }

  // ---- Trigger 1: prefetch after window.load via requestIdleCallback ----
  function schedulePrefetch() {
    if (!getPlaceholders().length) return;
    var doFetch = function () { fetchSubmenus(); };
    if ('requestIdleCallback' in window) {
      // 2 s timeout safety net — even on a busy page, kick off within 2 s.
      window.requestIdleCallback(doFetch, { timeout: 2000 });
    } else {
      setTimeout(doFetch, 250);
    }
  }
  if (document.readyState === 'complete') {
    schedulePrefetch();
  } else {
    window.addEventListener('load', schedulePrefetch, { once: true });
  }

  // ---- Trigger 2: kick off on first interaction with any trigger ----
  // Capture-phase delegation on document so we catch the event regardless
  // of bubbling-stoppers in Stiletto's Navigation() handler.
  function onTriggerInteract(e) {
    var target = e.target;
    if (!target || !target.closest) return;
    if (
      target.closest('[data-meganav-trigger]') ||
      target.closest('[data-dropdown-trigger]') ||
      target.closest('[data-lazy-submenu]')
    ) {
      fetchSubmenus();
    }
  }
  document.addEventListener('mouseover', onTriggerInteract, { capture: true, passive: true });
  document.addEventListener('focusin', onTriggerInteract, { capture: true, passive: true });
  document.addEventListener('touchstart', onTriggerInteract, { capture: true, passive: true });
})();
